import type { ResultOf } from '@graphql-typed-document-node/core';

import Skeleton from '@mui/material/Skeleton';

import {
  getFragmentData,
  graphql,
  ResolvableModeEnum,
} from '@e-socna/data-layer';
import { invariant, isDefined } from '@e-socna/utils';
import { EMPTY_STRING } from '@e-socna/empty';
import type { FragmentType, Id } from '@e-socna/data-layer';

import { wrapExpression } from '../../utils/handlebars-expression.util';
import { checkIfIsId } from '../../utils/check-if-is-id.util';
import { N_A } from '../../constants/labels.constant';

import { useContentGenerator } from '../DocumentEditor/hooks/useContentGenerator.hook';

import { useResolvableNormalizer } from './hooks/useResolvableNormalizer';
import { PrimaryWrapper as PrimaryWrapperCmp } from './PrimaryWrapper';
import { SecondaryWrapper as SecondaryWrapperCmp } from './SecondaryWrapper';
import type { Props as BaseProps } from './types/props.type';

export const ResolvableIdCmp_ResolvableId_Fragment = graphql(`
  fragment ResolvableIdCmp_ResolvableId_Fragment on ResolvableId {
    __typename
    mode
    resolverId
    value
  }
`);

export type ResolvableIdCmp_ResolvableId = ResultOf<
  typeof ResolvableIdCmp_ResolvableId_Fragment
>;

interface Props extends BaseProps {
  readonly idMap: Record<Id, Readonly<{ label: string }>>;
  readonly resolvableId: FragmentType<
    typeof ResolvableIdCmp_ResolvableId_Fragment
  >;
}

export const ResolvableId = ({
  idMap,
  PrimaryWrapper = PrimaryWrapperCmp,
  resolvableFieldSettings,
  resolvableId: pResolvableId,
  SecondaryWrapper = SecondaryWrapperCmp,
}: Readonly<Props>) => {
  const getLabel = (id: Id) => {
    const label = idMap[id]?.label;

    invariant(isDefined(label), `Unknown id: ${id}`);

    return label;
  };

  const resolvableId = getFragmentData(
    ResolvableIdCmp_ResolvableId_Fragment,
    pResolvableId,
  );

  const normalizedId = useResolvableNormalizer(resolvableId);

  const { enableDirectGeneration, primaryMode } = resolvableFieldSettings;

  const { data: idData, isSuccess: idIsSuccess } = useContentGenerator({
    enabled:
      normalizedId.mode === ResolvableModeEnum.Dynamic &&
      enableDirectGeneration,
    template:
      normalizedId.mode === ResolvableModeEnum.Dynamic
        ? wrapExpression(normalizedId.resolverCode)
        : EMPTY_STRING,
  });

  if (normalizedId.mode === ResolvableModeEnum.Static) {
    if (!isDefined(normalizedId.value)) {
      return <PrimaryWrapper>{N_A}</PrimaryWrapper>;
    }

    const label = getLabel(normalizedId.value);

    invariant(isDefined(label), `Unknown resolvableId: ${normalizedId.value}`);

    return <PrimaryWrapper>{label}</PrimaryWrapper>;
  }

  const command = wrapExpression(normalizedId.merged);

  if (!enableDirectGeneration) {
    return <PrimaryWrapper>{command}</PrimaryWrapper>;
  }

  if (!idIsSuccess) {
    return (
      <Skeleton sx={{ display: 'inline-block' }} variant="text" width={80} />
    );
  }

  const valueAsId = isDefined(idData?.generated)
    ? idData.generated
    : normalizedId.value;

  if (!isDefined(valueAsId)) {
    return <PrimaryWrapper>{N_A}</PrimaryWrapper>;
  }

  invariant(checkIfIsId(valueAsId), `Invalid id: ${valueAsId}`);

  const formattedValue = getLabel(valueAsId);

  return primaryMode === ResolvableModeEnum.Static ? (
    <>
      <PrimaryWrapper>{formattedValue}</PrimaryWrapper>{' '}
      <SecondaryWrapper>{command}</SecondaryWrapper>
    </>
  ) : (
    <>
      <PrimaryWrapper>{command}</PrimaryWrapper>{' '}
      <SecondaryWrapper>{formattedValue}</SecondaryWrapper>
    </>
  );
};
